import moment from "moment"
import ProductsService from '@/services/ProductsService'

// --- helpers (pure) ---
const toNum = (v, def = 0) => (v === '' || v == null ? def : Number(v)) || def

const getProductId = p => (p?.productId?._id || p?.productId || '').toString().trim()

/** InfaktCustomerPicker form keys → invoice payload keys (match existing infakt_client_* schema). */
export const INFAKT_CUSTOMER_FIELD_MAP = {
	nazwaFirmy: 'infakt_client_company_name',
	nip: 'infakt_client_nip',
	nazwaUlicy: 'infakt_client_street',
	nrBudynku: 'infakt_client_street_number',
	nrLokalu: 'infakt_client_flat_number',
	kodPocztowy: 'infakt_client_postal_code',
	nazwaMiejscowosci: 'infakt_client_city',
	adresEmail: 'infakt_client_email',
	infaktClientId: 'infakt_client_id',
}

/**
 * Merge filled InfaktCustomerPicker fields into an invoice payload.
 * Empty / whitespace-only values are skipped.
 */
export const appendInfaktCustomerToPayload = (payload, customer) => {
	if (!payload || !customer) return payload

	for (const [formKey, payloadKey] of Object.entries(INFAKT_CUSTOMER_FIELD_MAP)) {
		const value = String(customer[formKey] ?? '').trim()
		if (value) payload[payloadKey] = value
	}

	return payload
}

/** Build InfaktCustomerPicker value from an invoice document (edit hydrate). */
export const buildInfaktCustomerFromInvoice = (invoice) => {
	if (!invoice) return null

	const next = {}
	let hasAny = false
	for (const [formKey, payloadKey] of Object.entries(INFAKT_CUSTOMER_FIELD_MAP)) {
		const value = String(invoice[payloadKey] ?? '').trim()
		next[formKey] = value
		if (value) hasAny = true
	}

	return hasAny ? next : null
}

/**
 * Persist invoice line catalog fields back to linked products.
 * Call on invoice save (not while editing fields).
 */
export const syncProductsToCatalog = async (products = []) => {
	const updates = []

	for (const p of products) {
		const productId = getProductId(p)
		const name = (p?.name || '').trim()
		if (!productId || !name) continue

		updates.push(
			ProductsService.updateProduct(productId, {
				name,
				sku: p.sku || '',
				article_number: p.article_number || '',
				price: p.price,
				taxPercent: p.taxPercent,
			})
		)
	}

	if (!updates.length) return { success: true, error: null }

	const results = await Promise.all(updates)
	const failed = results.find(r => r?.error)
	if (failed) return { success: false, error: failed.error }
	return { success: true, error: null }
}

// ---- payload builder (single pass for sums) ----
export const buildPayload = f => {
	// console.log(`TCL -> submitInvoice.js:17 -> buildPayload -> f:`, f)
	const rows = []
	let taxTotal = 0
	let subtotal = 0

	for (const p of f.products || []) {
		const price = toNum(p.price)
		const count = toNum(p.count)
		const taxPercent = toNum(p.taxPercent)

		const base = price * count
		const taxValue = +(base * (taxPercent / 100)).toFixed(2)
		const total = +(base + taxValue).toFixed(2)

		rows.push({
			name: p.name?.trim() || '',
			price,
			count,
			taxPercent,
			subtotal: base,
			taxValue,
			total,
			...(p.productId ? { productId: String(p.productId) } : {}),
		})

		taxTotal += taxValue
		subtotal += base
	}

	taxTotal = +taxTotal.toFixed(2)
	subtotal = +subtotal.toFixed(2)
	const discountPercent = toNum(f.discountPercent)
	const discountValue = +((subtotal * discountPercent) / 100).toFixed(2)
	const taxDiscountValue = +((taxTotal * discountPercent) / 100).toFixed(2)
	const total = +(subtotal - discountValue + taxTotal - taxDiscountValue).toFixed(2) 

	const payload = {
		customerId: String(f.customerId),
		customerName: f.customerName || '', // keep updated display name on Details
		invoiceDate: moment(f.invoiceDate).format('YYYY-MM-DD'),
		dueDate: f.dueDate ? moment(f.dueDate).format('YYYY-MM-DD') : null,
		invoiceNumber: f.invoiceNumber || "",
		products: rows,
		discountPercent,
		subtotal,
		taxTotal,
		total,
		publicNotes: f.publicNotes || '',
		privateNotes: f.privateNotes || '',
		status: f.status,
	}

	if (f.paymentTerm) payload.paymentTerm = toYMD(f.paymentTerm)
	if (f.status === 'paid') {
		if (f.paymentType) payload.paymentType = f.paymentType
		if (f.paymentDate) payload.paidDate = toYMD(f.paymentDate)
	}

	return payload
}

// ---- server call ----
export const updateInvoiceOnServer = (InvoicesService, id, payload) => InvoicesService.updateInvoice(id, payload)
